import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from '../../store';
import { useMsal } from '@azure/msal-react';
import { useTranslation } from 'react-i18next';
import { Customer } from '../../interfaces/Customer';
import { dataActionCreators, Project } from '../../store/Data';
import { TeamsState } from '../../store/TeamsState';
import { Contact } from '../../interfaces/Contact';
import { customersActionCreators } from '../../store/customers';
import { ContactCustomerRole, ContactProjectRole, RelationShipTypes } from '../../interfaces/ContactCustomerRole';
import { ContactsActionCreators } from '../../store/contacts';
import { TokenHelper } from '../../TokenHelper';
import { Button, Dialog, DialogActions, DialogBody, DialogContent, DialogSurface, DialogTitle, Divider, Dropdown, Link, Option, Spinner, Text, Toast, ToastTitle, ToastTrigger, useToastController } from '@fluentui/react-components';
import { AddIcon, TrashCanIcon, useEditorStyles, useTabStyles } from '../Styles/Editor';
import { useCustomers } from '../Tools/ObjectLoader';

interface ContactDTO {
    contactid: string;
    customerid?: string;
    relationShipTypeId: string;
}
interface ContactOpsDTO {
    add: ContactDTO[];
    remove: ContactDTO[];
}

export interface ContactManagementProp {
    customerContactsEdit?: Customer;
    projectContactsEdit?: Project;
}
export const CustomersContactManagement = ({ customerContactsEdit, projectContactsEdit }: ContactManagementProp) => {
    // All data needed for edition should be loaded before calling the editor
    const { customers, customersLoading } = useCustomers();
    const contacts = useSelector<ApplicationState, Contact[] | undefined>((state: ApplicationState) => state.contactsState.contacts);
    const contactsLoading = useSelector((state: ApplicationState) => state.contactsState.contactsLoading);
    const teamsState = useSelector<ApplicationState, TeamsState>((state: ApplicationState) => state.teamsState);
    // Gen hooks
    const dispatch = useDispatch();
    const { instance } = useMsal();
    const { t } = useTranslation();
    const { dispatchToast, updateToast } = useToastController("NeoToasterUnique");
    const stylesTab = useTabStyles();
    const styles = useEditorStyles();
    // Context
    const [contextCustomer, setContextCustomer] = React.useState<Customer | null>(null);
    const [contextProject, setContextProject] = React.useState<Project | null>(null);
    const [contextContacts, setContextContacts] = React.useState<Contact[]>([]);
    const [contextExistingContacts, setContextExistingContacts] = React.useState<ContactProjectRole[]>([]);
    // Fields
    const [ContactsInEdition, setContactsInEdition] = React.useState<ContactProjectRole[]>([]);
    const [newContacts, setNewContacts] = React.useState<Contact>();
    const [newRole, setNewRole] = React.useState<RelationShipTypes>();
    // Fields validation
    // Form validation
    const [isSubmitValid, setSubmitValid] = React.useState(false);
    const [isAddValid, setAddValid] = React.useState(false);

    // Check if all needed datas are retrieven
    React.useEffect(() => {
        if (contacts === undefined && contactsLoading === false) {
            dispatch(ContactsActionCreators.requestContacts(instance, teamsState));
        }
    }, [instance, teamsState, contacts, contactsLoading]);

    // Init Context
    React.useEffect(() => {
        if (customerContactsEdit !== undefined) {
            setContextCustomer(customerContactsEdit);
        }
        if (projectContactsEdit !== undefined) {
            setContextProject(projectContactsEdit);
            if (customers !== undefined) {
                const c = customers.find(e => e.customerid === projectContactsEdit.customerid);
                if (c !== undefined) {
                    setContextCustomer(c);
                }
            }
        }
    }, [customerContactsEdit, projectContactsEdit, customers]);
    React.useEffect(() => {
        if (contextProject !== null) {
            if (contextCustomer !== null && contextCustomer.contacts !== null && contextCustomer.contacts !== undefined && contacts !== undefined) {
                const customerContacts: Contact[] = [];
                contextCustomer.contacts.forEach((c) => {
                    if (c.contactid) {
                        const cc = contacts.find(cl => cl.contactid === c.contactid);
                        if (cc !== undefined) {
                            customerContacts.push(cc);
                        }
                    }
                });
                setContextContacts(customerContacts);
            }
        }
        else if (contextCustomer !== null && contacts !== undefined) {
            setContextContacts(contacts);
        }
    }, [contacts, contextCustomer, contextProject]);

    React.useEffect(() => {
        if (contacts === undefined && !contactsLoading) {
            dispatch(ContactsActionCreators.requestContacts(instance, teamsState));
        }
        // Load customer contact if not already done
        if (contacts && !contactsLoading && contextCustomer && contextCustomer.customerid && !contextCustomer.contactsLoading && (contextCustomer.contacts === null || contextCustomer.contacts === undefined)) {
            dispatch(customersActionCreators.requestCustomerContacts(instance, teamsState, contextCustomer.customerid));
        }
        // Load project contact if not already done
        if (contacts && !contactsLoading && contextProject && contextProject.projectid && !contextProject.contactsLoading && (contextProject.contacts === null || contextProject.contacts === undefined)) {
            dispatch(dataActionCreators.requestProjectContacts(instance, teamsState, contextProject.projectid));
        }
    }, [instance, teamsState, contextCustomer, contacts, contactsLoading, contextProject]);

    // Set list of contacts for edition
    React.useEffect(() => {
        if (contextProject) {
            if (contextProject.contacts) {
                const contactsInEditionTemp: ContactProjectRole[] = contextProject.contacts.map((c) => {
                    return {
                        customerid: c.customerid,
                        contactid: c.contactid,
                        relationShipTypeId: c.relationShipTypeId,
                        projectid: c.projectid,
                    };
                });
                setContactsInEdition(contactsInEditionTemp);
                setContextExistingContacts([...contactsInEditionTemp]);
            }
        }
        else if (contextCustomer && contextCustomer.contacts) {
            const contactsInEditionTemp: ContactProjectRole[] = contextCustomer.contacts.map((c) => {
                return {
                    customerid: c.customerid,
                    contactid: c.contactid,
                    relationShipTypeId: c.relationShipTypeId,
                    projectid: null,
                };
            });
            setContactsInEdition(contactsInEditionTemp);
            setContextExistingContacts([...contactsInEditionTemp]);
        }
    }, [contextCustomer, contextProject]);

    // Check if new contact can be added.
    // Contact and Role must be selected, and combination must not already exist
    React.useEffect(() => {
        setAddValid(newContacts != undefined
            && newRole != undefined
            && ContactsInEdition
            && -1 === ContactsInEdition.findIndex(c => c.contactid && c.contactid === newContacts.contactid && c.relationShipTypeId === newRole)
        );
    }, [newContacts, newRole]);
    // Check if there is a change to anable the save/submit button
    React.useEffect(() => {
        let submitValid = false;
        if (ContactsInEdition !== null && contextCustomer != null && contextCustomer.contacts != null) {
            if (ContactsInEdition.length !== contextCustomer.contacts.length) {
                // items where added or removed
                submitValid = true;
            }
            else {
                // Check if existing items have changed
                contextCustomer.contacts.forEach(c => {
                    const cc = ContactsInEdition.find(ccc => ccc.contactid === c.contactid && ccc.relationShipTypeId === c.relationShipTypeId);
                    if (cc === undefined) {
                        submitValid = true;
                    }
                });
            }
        }
        setSubmitValid(submitValid);
    }, [ContactsInEdition, contextCustomer]);

    const submit = () => {
        const cOps: ContactOpsDTO = { add: [], remove: [] };
        if (ContactsInEdition !== null) {
            // Check for existing combination that does not exist anymore
            contextExistingContacts.forEach(c => {
                if (c.contactid && c.relationShipTypeId) {
                    const cc = ContactsInEdition.find(ccc => ccc.contactid === c.contactid && ccc.relationShipTypeId === c.relationShipTypeId);
                    if (cc === undefined) {
                        // Removed
                        cOps.remove.push({ contactid: c.contactid, customerid: contextCustomer !== null && contextCustomer.customerid !== null ? contextCustomer.customerid : undefined, relationShipTypeId: c.relationShipTypeId });
                    }
                }
            });
            // Check for new combinations
            ContactsInEdition.forEach(c => {
                if (c.contactid && c.relationShipTypeId) {
                    const cc = contextExistingContacts.find(ccc => ccc.contactid === c.contactid && ccc.relationShipTypeId === c.relationShipTypeId);
                    if (cc === undefined) {
                        // Add
                        cOps.add.push({ contactid: c.contactid, customerid: contextCustomer !== null && contextCustomer.customerid !== null ? contextCustomer.customerid : undefined, relationShipTypeId: c.relationShipTypeId });
                    }
                }
            });
        }

        TokenHelper.GetToken(instance, teamsState)
            .then(token => {
                if (contextCustomer && contextCustomer.customerid) {
                    if (token !== null) {
                        const toastId = (Math.random() + 1).toString(36).substring(7);
                        dispatchToast(
                            <Toast appearance="inverted">
                                <ToastTitle>
                                    {"Saving contacts " + (contextCustomer && contextCustomer.displayName ? contextCustomer.displayName : "-") + "..."}
                                </ToastTitle>
                            </Toast>,
                            { intent: "info", timeout: -1, position: "top-end", toastId: toastId }
                        );
                        fetch(contextProject === null
                            ? "api/Customers/" + contextCustomer.customerid + "/Contacts"
                            : "api/Projects/" + contextProject.projectid + "/Contacts", {
                            method: "POST",
                            headers: {
                                "Content-Type": "application/json",
                                authorization: "bearer " + token,
                            },
                            body: JSON.stringify(cOps),
                            mode: "cors",
                            cache: "default",
                        })
                            .then(response => {
                                if (response.status === 200) {
                                    updateToast({
                                        content: (
                                            <Toast appearance="inverted">
                                                <ToastTitle action={
                                                    <ToastTrigger>
                                                        <Link>Dismiss</Link>
                                                    </ToastTrigger>
                                                }>
                                                    Contacts saved
                                                </ToastTitle>
                                            </Toast>
                                        ),
                                        intent: "success",
                                        toastId,
                                        timeout: 5000,
                                    });
                                    return response.json() as Promise<ContactCustomerRole[]>;
                                }
                                else {
                                    updateToast({
                                        content: (
                                            <Toast appearance="inverted">
                                                <ToastTitle action={
                                                    <ToastTrigger>
                                                        <Link>Dismiss</Link>
                                                    </ToastTrigger>
                                                }>
                                                    Contacts save failed
                                                </ToastTitle>
                                            </Toast>
                                        ),
                                        intent: "error",
                                        toastId,
                                        timeout: 10000,
                                    });
                                }
                            }).then(data => {
                                if (data) {
                                    cOps.add.forEach((c) => dispatch(ContactsActionCreators.requestInvalidateContactRoles(c.contactid)));
                                    cOps.remove.forEach((c) => dispatch(ContactsActionCreators.requestInvalidateContactRoles(c.contactid)));
                                    if (contextProject === null) {
                                        if (contextCustomer.customerid) {
                                            dispatch(customersActionCreators.requestPatchCustomerContacts(contextCustomer.customerid, data));
                                            dispatch(customersActionCreators.requestCustomerContactsEdition(null));
                                        }
                                    }
                                    else {
                                        if (contextProject.projectid) {
                                            dispatch(dataActionCreators.requestPatchProjectContacts(contextProject.projectid, data as ContactProjectRole[]));
                                            dispatch(customersActionCreators.requestCustomerContactsEdition(null));
                                        }
                                    }
                                }
                            });

                    }
                }
            });
    };

    const items: JSX.Element[] = [];
    if (ContactsInEdition) {
        ContactsInEdition//.sort(SortHelper.CustomerByName)
            .forEach((c, index) => {
                const contact = contacts ? contacts.find(cl => cl.contactid === c.contactid) : undefined;
                items.push(<div className={stylesTab.ContactEditorEntry}>
                    <Text style={{ alignSelf: "center" }}>{contact ? (contact.firstName + " " + contact.lastName) : "?"}</Text>
                    <Dropdown
                        className={styles.fluid}
                        onOptionSelect={(event, data) => {
                            if (data.optionValue !== undefined) {
                                const newContactsInEdition = [...ContactsInEdition];
                                newContactsInEdition[index].relationShipTypeId = data.optionValue as RelationShipTypes;
                                setContactsInEdition(newContactsInEdition);
                            }
                        }}
                        placeholder="Choose a role"
                        value={c.relationShipTypeId !== null ? t(c.relationShipTypeId) : undefined}
                        selectedOptions={c.relationShipTypeId !== null ? [t(c.relationShipTypeId)] : []}
                    >
                        <Option key="OWNER" value="OWNER">{t("OWNER")}</Option>
                        <Option key="MEMBER" value="MEMBER">{t("MEMBER")}</Option>
                        <Option key="FREELANCE" value="FREELANCE">{t("FREELANCE")}</Option>
                        <Option key="PROJECTMGR" value="PROJECTMGR">{t("PROJECTMGR")}</Option>
                        <Option key="CONTRACTMGR" value="CONTRACTMGR">{t("CONTRACTMGR")}</Option>
                        <Option key="SALESMGR" value="SALESMGR">{t("SALESMGR")}</Option>
                        <Option key="INVOICE" value="INVOICE">{t("INVOICE")}</Option>
                    </Dropdown>
                    <div className={stylesTab.MarginLeftAuto}>
                        <Button
                            icon={<TrashCanIcon />}
                            appearance="subtle"
                            aria-label="Delete"
                            onClick={() => {
                                const f = ContactsInEdition.filter(e => !(e.contactid === c.contactid && e.relationShipTypeId === c.relationShipTypeId));
                                setContactsInEdition(f);
                            }} />
                    </div>
                </div>);
            });
    }

    return <Dialog
        open
        modalType="modal">
        <DialogSurface>
            <DialogBody>
                <DialogTitle>{"Edit contacts : " + (contextCustomer ? contextCustomer.displayName : "-")}</DialogTitle>
                <DialogContent>
                    <div className={stylesTab.ContactEditorEntryLine}>
                        <Divider />
                        <div className={stylesTab.ContactEditorEntry}>
                            <Dropdown
                                className={styles.fluid}
                                onOptionSelect={(event, data) => {
                                    if (contextContacts) {
                                        setNewContacts(contextContacts.find(c => c.contactid === data.optionValue));
                                    }
                                }}
                                placeholder="Choose a contact"
                                value={newContacts ? (newContacts.firstName + " " + newContacts.lastName) : ""}
                                selectedOptions={newContacts && newContacts.contactid ? [newContacts.contactid] : []}
                            >
                                {
                                    contextContacts && contextContacts
                                        .map(c =>
                                            <Option key={c.contactid ? c.contactid : ""} value={c.contactid ? c.contactid : ""}>{c.firstName + " " + c.lastName}</Option>
                                        )
                                }
                            </Dropdown>
                            <Dropdown
                                className={styles.fluid}
                                onOptionSelect={(event, data) => {
                                    setNewRole(data.optionValue ? data.optionValue as RelationShipTypes : "MEMBER");
                                }}
                                placeholder="Choose a role"
                                value={newRole ? t(newRole) : ""}
                                selectedOptions={newRole ? [newRole] : []}
                            >
                                <Option key="OWNER" value="OWNER">{t("OWNER")}</Option>
                                <Option key="MEMBER" value="MEMBER">{t("MEMBER")}</Option>
                                <Option key="FREELANCE" value="FREELANCE">{t("FREELANCE")}</Option>
                                <Option key="PROJECTMGR" value="PROJECTMGR">{t("PROJECTMGR")}</Option>
                                <Option key="CONTRACTMGR" value="CONTRACTMGR">{t("CONTRACTMGR")}</Option>
                                <Option key="SALESMGR" value="SALESMGR">{t("SALESMGR")}</Option>
                                <Option key="INVOICE" value="INVOICE">{t("INVOICE")}</Option>
                            </Dropdown>
                            <div className={stylesTab.MarginLeftAuto}>
                                <Button
                                    icon={<AddIcon />}
                                    appearance="subtle"
                                    aria-label={t('Edit')}
                                    disabled={!isAddValid}
                                    onClick={() => {
                                        if (ContactsInEdition
                                            && newContacts && newContacts.contactid
                                            && contextCustomer && contextCustomer.customerid
                                        ) {
                                            setContactsInEdition([...ContactsInEdition, { contactid: newContacts.contactid, customerid: contextCustomer.customerid, projectid: contextProject != null ? contextProject.projectid : null, relationShipTypeId: newRole ? newRole : null }]);
                                            setNewContacts(undefined);
                                            setNewRole(undefined);
                                        }
                                    }} />
                            </div>
                        </div>
                        <Divider />
                        {
                            (
                                contactsLoading
                                || customersLoading
                                || (contextCustomer && contextCustomer.contactsLoading)
                                || (contextProject && contextProject.contactsLoading)
                            )
                                ?
                                <Spinner size="large" label={t('Loading')} />
                                :
                                items.length > 0
                                    ? items.map(i => i)
                                    : <Text>No Contacts defined</Text>
                        }
                        <Divider />
                    </div>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => dispatch(customersActionCreators.requestCustomerContactsEdition(null))}
                    >
                        {t("Cancel")}
                    </Button>
                    <Button
                        onClick={() => submit()}
                        type="submit"
                        form="my-form-id"
                        disabled={!isSubmitValid}
                    >
                        {t("Save")}
                    </Button>
                </DialogActions>
            </DialogBody>
        </DialogSurface >
    </Dialog >;
};